import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { lazy, Suspense, useMemo } from 'react';

import { getAccountCards } from 'services/account';

import { useBrowsing } from 'hooks/use-browsing';

import { logger } from 'helpers/logging';

import type { IconVariants } from 'core/generic/elements/icons';

import styles from './index.module.scss';
import stylesDefault from './styles.module.scss';

const Button = lazy(() => import('core/generic/elements/button'));
const CardCategory = lazy(
	() => import('core/generic/components/cards/card-category')
);
const Loading = lazy(() => import('core/generic/elements/loading'));
const ErrorPage = lazy(() => import('@store/components/error-page/error-page'));
const CityzensFeedAnchor = lazy(
	() =>
		import(
			'@myaccount/components/cityzens-feed-anchor/cityzens-feed-anchor'
		)
);

export function Index() {
	const { query } = useBrowsing();
	const isFromApp = query.fromapp !== undefined ? true : false;

	const {
		data: accountCardsQuery,
		isLoading: accountCardsLoading,
		error: accountCardsError,
	} = useQuery({
		queryKey: ['account-cards'],
		queryFn: async () => {
			try {
				const { data } = await getAccountCards();
				return data;
			} catch (error) {
				logger.error('Failed to load Account Cards.', {
					statusCode: (error as AxiosError)?.response?.status,
				});
				throw error;
			}
		},
	});

	const formatUrl = (url: string): string => {
		if (!url) return url;
		return url.endsWith('/') ? url.slice(0, -1) : url;
	};

	const featuredCards = useMemo(() => {
		if (!accountCardsQuery?.cards) return undefined;
		return accountCardsQuery.cards
			.filter((card) => card.alias !== 'cityzensFeed')
			.map((card) => ({
				...card,
				url: {
					...card.url,
					url: formatUrl(card.url.url),
				},
			}));
	}, [accountCardsQuery]);

	const cityzensCard = accountCardsQuery?.cards.find(
		(card) => card.alias === 'cityzensFeed'
	);

	if (accountCardsLoading) {
		return (
			<Suspense fallback={null}>
				<div className={stylesDefault.loading}>
					<Loading dimension="large" isWithCrest />
				</div>
			</Suspense>
		);
	}

	if (accountCardsError) {
		return (
			<Suspense fallback={null}>
				<ErrorPage />
			</Suspense>
		);
	}

	return (
		<Suspense fallback={null}>
			<div className={styles.container}>
				<div className="row">
					<div className="col-12">
						<div className={styles['features-list']}>
							{featuredCards?.map((card) => (
								<CardCategory
									key={card.title}
									title={card.title}
									description={card.description}
									icon={card.iconId as IconVariants}
									url={`${card.url.url}${
										isFromApp ? '?fromapp=' : ''
									}`}
									isAnchor={card.url.isExternalLink}
									type="elevated"
									showArrow
									mobileMode="compact"
									titleDimension="xsmall"
									target={card.url.target}
								/>
							))}
						</div>
					</div>
				</div>

				<div className="row">
					<div className="col-12">
						{cityzensCard ? (
							<div className={styles['cityzens-feed-anchor']}>
								<CityzensFeedAnchor {...cityzensCard} />
							</div>
						) : null}
					</div>
				</div>

				<div className="row">
					<div className="col-12 col-md-4 col-offset-md-4">
						<div className={styles['sing-out-button']}>
							<Button
								fullWidth
								variant="tertiary"
								label="Sign out"
								href="/logout"
							/>
						</div>
					</div>
				</div>
			</div>
		</Suspense>
	);
}

export default Index;
